import { Controller } from '@hotwired/stimulus';
import { SearchesMixin } from '../../../mixins/searches_mixin';

export default class extends Controller {
  static targets = [
    'group',
    'causeIdInputWrapper',
    'organizationIdInputWrapper',
  ];
  static values = {
    causeScope: { type: String, default: 'available_campaigns' },
  };

  connect() {
    this.readonly = this.element.dataset.readonly === 'true';
    this.checkboxes =
      Array.from(this.element.querySelectorAll('input[type="checkbox"]')) || [];
    this.initialize();
  }

  initialize() {
    this.groupTargets.forEach((group) => this.initializeGroup(group));
    if (!this.readonly) {
      this.initializeSearches();
      this.element
        .querySelectorAll('[data-limit]')
        .forEach((input) => this.updateCounter({ target: input }));
    }
    this.updateCheckboxState();
  }

  initializeGroup(group) {
    const index = parseInt(group.dataset.optionIndex);
    const destroyInput = group.querySelector('input[name*="[_destroy]"]');
    const checkbox = this.getCheckboxByIndex(index);
    const isVisible = destroyInput && destroyInput.value === '0';

    this.updateGroupVisibility(group, isVisible);
    if (checkbox) checkbox.checked = isVisible;
    if (index === 0 || isVisible) this.initializeGroupVisibility(group);
  }

  updateCounter({ target: input }) {
    const limit = parseInt(input.dataset.limit);
    if (!limit) return;

    const wrapper = input.closest('.input');
    const hint = wrapper && wrapper.nextElementSibling;
    if (!hint || !hint.classList.contains('hint')) return;

    const counter =
      hint.querySelector('.char-counter') || this.createCounter(hint);
    const currentLength = input.value.length;
    counter.textContent = `${currentLength}/${limit} znaków`;
    counter.classList.toggle('red-text', currentLength > limit);
  }

  createCounter(hint) {
    const counter = document.createElement('span');
    counter.classList.add('char-counter');
    const hintText = document.createElement('span');
    hintText.classList.add('hint-text');
    hintText.append(...hint.childNodes);
    hint.append(hintText, counter);
    Object.assign(hint.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    });
    return counter;
  }

  toggle({ target: checkbox }) {
    if (this.readonly) return;

    const index = parseInt(checkbox.dataset.optionIndex);
    const group = this.getGroupByIndex(index);
    if (!group) return;

    this.updateGroupVisibility(group, checkbox.checked);
    if (!checkbox.checked) {
      this.clearFormFields(group);
      this.clearErrorMessages(group);
      this.resetSubsequentCheckboxes(index);
    }
    this.updateCheckboxState();
  }

  updateGroupVisibility(group, isVisible) {
    group.style.display = isVisible ? 'block' : 'none';
    const destroyInput = group.querySelector('input[name*="[_destroy]"]');
    if (destroyInput) destroyInput.value = isVisible ? '0' : '1';
  }

  resetSubsequentCheckboxes(currentIndex) {
    if (!this.checkboxes) return;
    this.checkboxes
      .filter((cb) => parseInt(cb.dataset.optionIndex) > currentIndex)
      .forEach((checkbox) => {
        checkbox.checked = false;
        const group = this.getGroupByIndex(
          parseInt(checkbox.dataset.optionIndex),
        );
        if (group) {
          this.updateGroupVisibility(group, false);
          this.clearFormFields(group);
        }
      });
  }

  updateCheckboxState() {
    if (this.readonly || !this.checkboxes) return;

    this.checkboxes
      .sort(
        (a, b) =>
          parseInt(a.dataset.optionIndex) - parseInt(b.dataset.optionIndex),
      )
      .forEach((checkbox, i) => {
        checkbox.disabled = i > 0 && !this.checkboxes[i - 1].checked;
      });
  }

  clear_target({ params: { causeVisible, organizationVisible, optionIndex } }) {
    const group = this.getGroupByIndex(parseInt(optionIndex));
    if (!group) return;

    const causeWrapper = group.querySelector(
      '[data-panel--microdonation-option-sets--form-target="causeIdInputWrapper"]',
    );
    const organizationWrapper = group.querySelector(
      '[data-panel--microdonation-option-sets--form-target="organizationIdInputWrapper"]',
    );

    this.toggleVisibility(
      causeWrapper,
      causeVisible,
      organizationWrapper,
      organizationVisible,
    );
    [causeWrapper, organizationWrapper].forEach((wrapper) => {
      if (wrapper) {
        const input = wrapper.querySelector('input');
        if (input) input.value = '';
      }
    });
    this.clearResultsForOption(optionIndex);
  }

  clearFormFields(group) {
    group
      .querySelectorAll(
        'input[type="text"], input[type="email"], input[type="number"], textarea, select',
      )
      .forEach((input) => {
        if (
          input.type !== 'file'
          && input.tagName !== 'SELECT'
          && !input.name.includes('[position]')
          && !input.name.includes('[_destroy]')
        ) {
          input.value = '';
        } else if (input.tagName === 'SELECT') {
          input.selectedIndex = 0;
        }
      });
    this.clearResultsForOption(parseInt(group.dataset.optionIndex));
  }

  enforceCharacterLimit({ target }) {
    target.value = target.value.slice(0, 7);
  }

  initializeGroupVisibility(group) {
    const causeRadio = group.querySelector(
      'input[type="radio"][value="Cause"]',
    );
    const organizationRadio = group.querySelector(
      'input[type="radio"][value="Organization"]',
    );
    const causeWrapper = group.querySelector(
      '[data-panel--microdonation-option-sets--form-target="causeIdInputWrapper"]',
    );
    const organizationWrapper = group.querySelector(
      '[data-panel--microdonation-option-sets--form-target="organizationIdInputWrapper"]',
    );

    const visibility = {
      cause:
        causeRadio
        && (causeRadio.checked
          || !organizationRadio
          || !organizationRadio.checked),
      organization: organizationRadio && organizationRadio.checked,
    };

    this.toggleVisibility(
      causeWrapper,
      visibility.cause,
      organizationWrapper,
      visibility.organization,
    );
    if (
      causeRadio
      && !causeRadio.checked
      && (!organizationRadio || !organizationRadio.checked)
    ) {
      causeRadio.checked = true;
    }
  }

  toggleVisibility(
    causeWrapper,
    causeVisible,
    organizationWrapper,
    organizationVisible,
  ) {
    if (causeWrapper) causeWrapper.classList.toggle('hidden', !causeVisible);
    if (organizationWrapper)
      organizationWrapper.classList.toggle('hidden', !organizationVisible);
  }

  initializeSearches() {
    this.groupTargets.forEach((group, index) => {
      const causeWrapper = group.querySelector(
        '[data-panel--microdonation-option-sets--form-target="causeIdInputWrapper"]',
      );
      const organizationWrapper = group.querySelector(
        '[data-panel--microdonation-option-sets--form-target="organizationIdInputWrapper"]',
      );

      if (causeWrapper && causeWrapper.querySelector('input'))
        this.initializeSearch(
          causeWrapper,
          'Cause',
          index,
          this.causeScopeValue,
        );
      if (organizationWrapper && organizationWrapper.querySelector('input'))
        this.initializeSearch(organizationWrapper, 'Organization', index);
    });
  }

  initializeSearch(wrapper, klass, optionIndex, scope = null) {
    const searchController = { element: wrapper };
    SearchesMixin(searchController, klass);
    searchController.initializeSearch(
      {
        selector: wrapper,
        query: scope ? { scope } : {},
        noResultsMessage: 'Nie znaleziono pasujących wyników',
      },
      {
        onSelect: (result) => {
          const input = wrapper.querySelector('input');
          if (input) {
            input.value = result.id;
            const displayInput = wrapper.querySelector('.prompt.search');
            if (displayInput)
              displayInput.value = result.title || result.name || result.id;
          }
          this.clearResultsForOption(optionIndex);
        },
      },
    );
  }

  getGroupByIndex(index) {
    return this.groupTargets.find(
      (group) => parseInt(group.dataset.optionIndex) === index,
    );
  }

  getCheckboxByIndex(index) {
    return (
      (this.checkboxes
        && this.checkboxes.find(
          (checkbox) => parseInt(checkbox.dataset.optionIndex) === index,
        ))
      || null
    );
  }

  clearResultsForOption(optionIndex) {
    const group = this.getGroupByIndex(optionIndex);
    if (!group) return;

    ['causeIdInputWrapper', 'organizationIdInputWrapper'].forEach((target) => {
      const wrapper = group.querySelector(
        `[data-panel--microdonation-option-sets--form-target="${target}"]`,
      );
      if (wrapper) {
        const results = wrapper.querySelector('.results');
        if (results) {
          results.innerHTML = '';
          results.classList.add('hidden');
        }
      }
    });
  }

  clearErrorMessages(group) {
    const previousSibling = group.previousElementSibling;
    if (previousSibling) {
      const errorMessages = previousSibling.querySelector(
        '.ui.negative.message',
      );
      if (errorMessages) errorMessages.style.display = 'none';
    }
  }

  closeErrorMessage(event) {
    const message = event.target.closest('.ui.negative.message');
    if (message) message.style.display = 'none';
  }
}
